import { Types, metaData } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { annotation } from '@cornerstonejs/tools';
import { mapAnnotationToMeasurement } from '@/state/slices/measurementSession';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/models/spectralDoppler';
import type { SpectralDopplerValues } from '@/models/spectralDoppler';
import type { AIMeasurement } from '@/models/aiScan';
import type { AppDispatch } from '@/state/store';
import {
  pixelToWorld,
  worldToPixel,
} from '../../tools/SpectralDoppler/worldPixelCoords';
import {
  openEnvelopeOnBaseline,
  polylineSignature,
} from '../../tools/SpectralDoppler/geometry';
import {
  resolveSpectralRegion,
  buildCalibrationFromRegion,
} from '../../tools/SpectralDoppler/calibrationRegion';
import type { UltrasoundRegionsMeta } from '../../tools/SpectralDoppler/SpectralDopplerBaseTool';
import { getBaseAnnotationStyle } from '../../utils/annotationStyle';

/**
 * EchoMeasure v2 doppler waveform annotations both arrive as FREEHAND, sharing
 * the type with LV Simpson-volume contours. A VTI envelope is recognised by the
 * doppler `_vti` canonical-measurement convention so it routes to the doppler
 * envelope renderer instead of the volume (Simpson) path.
 *
 * ponytail: convention match for now — replace with the explicit per-measurement
 * tool field once the backend ships it (a tool will attach to each measurement).
 */
export function isDopplerVtiMeasurement(ai: AIMeasurement): boolean {
  // canonicalMeasurement is a UUID; the readable slug (e.g. "MV_E_VTI") is on
  // canonicalMeasurementKey. Match the key or the FREEHAND VTI routes to the
  // Simpson volume path (AP-616).
  return (
    !!ai.canonicalMeasurementKey && /_vti$/i.test(ai.canonicalMeasurementKey)
  );
}

/** AI velocity → cm/s. EchoMeasure reports cm/s; tolerate m/s just in case. */
function toCmS(value: number, unit: string): number {
  return unit?.toLowerCase() === 'm/s' ? value * 100 : value;
}

/**
 * EchoMeasure emits spectral-Doppler POINT/VTI coords in **native image-pixel
 * space** — the same grid the DICOM ultrasound-region bbox lives in. The
 * viewport draws in world space, which is NOT 1:1 with native pixels on a video
 * viewport, so a raw pixel point placed as a world coord lands in the wrong
 * spot. Convert through the viewport's own index→world transform (`pixelToWorld`,
 * i.e. vtk.indexToWorld) — the exact inverse the spectral tools use
 * (`worldToPixel`) when deriving velocity, so AI and manual annotations share
 * one coordinate space.
 *
 * Returns null if the viewport's imageData isn't ready yet — the caller returns
 * false so hydration retries on the next render.
 */
function pixelPointsToWorld(
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport,
  points: Types.Point3[]
): Types.Point3[] | null {
  const world = points.map(([px, py]) => pixelToWorld(viewport, px, py));
  return world.includes(null) ? null : (world as Types.Point3[]);
}

/**
 * The referencedImageId must equal what the viewport reports as its current
 * image, or Cornerstone drops the annotation before painting. Mirrors the human
 * probe path (hydrateProbeAnnotation):
 *  - Single-frame spectral stills are STACK viewports — getCurrentImageId() is a
 *    real (non-"video:") id, so use it verbatim.
 *  - Multi-frame sweeps are VIDEO viewports — getCurrentImageId() is absent or a
 *    "video:" id for the *current* frame, so anchor to the annotation's own
 *    captured frame ("video:<dicom>/frames/<N>", 1-based).
 */
export function resolveDopplerImageId(
  viewport: { getCurrentImageId?: () => string | undefined },
  dicomUuid: string,
  frameNumber: number
): string {
  const live = viewport.getCurrentImageId?.();
  if (live && !live.startsWith('video:')) return live;
  return `video:${dicomUuid}/frames/${frameNumber}`;
}

interface HydrateAiDopplerArgs {
  ai: AIMeasurement;
  uid: string;
  aiLabel: string;
  /** Worksheet abbreviation (e.g. "MV E") — powers the hover chip + header. */
  abbreviation?: string;
  /** Keep the selected style if this marker was selected before it hydrated. */
  isSelected?: boolean;
  frameOfReferenceUID: string;
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport;
  camera: ReturnType<
    (CornerstoneTypes.IStackViewport | Types.IVideoViewport)['getCamera']
  >;
  canEdit: boolean;
  dispatch: AppDispatch;
}

interface AddDopplerAnnotationArgs extends HydrateAiDopplerArgs {
  /** Resolved via resolveDopplerImageId — stack imageId or `video:<dicom>/frames/<N>`. */
  referencedImageId: string;
  toolName: string;
  /** World points — native pixel coords already converted via pixelToWorld. */
  points: Types.Point3[];
  spectralToolVariant: 'probe' | 'vti';
  spectralValues: SpectralDopplerValues;
  /** VTI renders from data.polyline; probe renders from data.handles.points. */
  withPolyline: boolean;
}

/**
 * Shared add for both doppler variants. `spectralCalibration` is rebuilt from
 * viewport metadata (best effort) but the render path only recomputes while a
 * reader is actively dragging (see VelocityProbeTool) — so the authoritative AI
 * value in `spectralValues` is preserved until adoption. `data.label` carries
 * the AI label; `data.abbreviation` powers the shared hover chip + header.
 */
function addDopplerAnnotation({
  ai,
  uid,
  aiLabel,
  abbreviation,
  isSelected,
  referencedImageId,
  frameOfReferenceUID,
  viewport,
  camera,
  canEdit,
  dispatch,
  toolName,
  points,
  spectralToolVariant,
  spectralValues,
  withPolyline,
}: AddDopplerAnnotationArgs): boolean {
  try {
    const anchor = points[0] ?? ([0, 0, 0] as Types.Point3);
    const endpointHandles =
      points.length >= 2 ? [points[0], points[points.length - 1]] : points;

    // Rebuild the region calibration from viewport metadata (same lookup the
    // tool uses when drawing) so a reader dragging the marker re-derives the
    // value on the way to adopting it. Best effort — absence just means no
    // recompute, never a missing marker.
    const imageId =
      (
        viewport as unknown as {
          getCurrentImageId?: () => string | undefined;
        }
      ).getCurrentImageId?.() ?? '';
    const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
      | UltrasoundRegionsMeta
      | undefined;
    const { region } = resolveSpectralRegion(
      regionsMeta?.regions,
      worldToPixel(viewport, anchor)
    );
    const spectralCalibration = region
      ? buildCalibrationFromRegion(region)
      : undefined;

    annotation.state.addAnnotation(
      {
        annotationUID: uid,
        data: {
          handles: {
            // VTITraceTool's contract is [first, last] — handleSelectedCallback
            // maps handle 0/1 onto the polyline ends. Passing the whole envelope
            // (80-110 points) makes every vertex a grab target, so Cornerstone's
            // handle check always beats isPointNearTool and the line can't be
            // dragged. The full geometry lives on `polyline`, which renders.
            points: withPolyline ? endpointHandles : points,
            activeHandleIndex: null,
            textBox: {
              hasMoved: false,
              worldPosition: anchor,
              worldBoundingBox: {
                topLeft: anchor,
                topRight: anchor,
                bottomLeft: anchor,
                bottomRight: anchor,
              },
            },
          },
          ...(withPolyline ? { polyline: points } : {}),
          label: `AI: ${aiLabel}`,
          measurementLabel: `AI: ${aiLabel}`,
          abbreviation,
          isAI: true,
          spectralToolVariant,
          spectralValues,
          spectralCalibration,
          // Identity of the envelope as encephalon shipped it. The tool keeps
          // the reported VTI while this still matches, and re-derives once a
          // reader reshapes the trace (see VTITraceTool.renderAnnotation).
          ...(withPolyline ? { aiPolylineSig: polylineSignature(points) } : {}),
          cachedStats: {},
        },
        highlighted: false,
        autoGenerated: false,
        // false → tool render keeps spectralValues (recompute is calibration-gated).
        invalidated: false,
        isLocked: !canEdit,
        isVisible: true,
        metadata: {
          toolName,
          FrameOfReferenceUID: frameOfReferenceUID,
          referencedImageId,
          viewPlaneNormal: [0, 0, 1] as Types.Point3,
          viewUp: camera.viewUp ?? ([0, -1, 0] as Types.Point3),
          cameraFocalPoint: camera.focalPoint ?? anchor,
          cameraPosition: camera.position ?? ([0, 0, 0] as Types.Point3),
        },
      },
      viewport.element
    );

    annotation.locking.setAnnotationLocked(uid, !canEdit);

    // Style through the shared source-color palette (mint for AI) so hover /
    // selection behave like Length instead of the always-on Cornerstone default
    // that vanished on first hover (AP-695).
    annotation.config.style.setAnnotationStyles(
      uid,
      getBaseAnnotationStyle(
        { data: { isAI: true } },
        { isSelected: !!isSelected }
      )
    );

    dispatch(
      mapAnnotationToMeasurement({
        annotationUID: uid,
        canonicalMeasurement: ai.canonicalMeasurement,
      })
    );

    return true;
  } catch {
    // Viewport not fully ready — retries on next render.
    return false;
  }
}

/**
 * Hydrate a doppler POINT (E/A peak velocity) as a VelocityProbe annotation —
 * renders a crosshair marker + value label at the single peak point.
 */
export function hydrateAiDopplerPoint(args: HydrateAiDopplerArgs): boolean {
  const ann = args.ai.annotation!;
  const rawPoint = ann.points[0];
  if (!rawPoint) return false;
  const world = pixelPointsToWorld(args.viewport, [
    [...rawPoint] as Types.Point3,
  ]);
  if (!world) return false;
  return addDopplerAnnotation({
    ...args,
    referencedImageId: resolveDopplerImageId(
      args.viewport,
      ann.dicom,
      ann.frameNumber
    ),
    toolName: SPECTRAL_DOPPLER_TOOL_NAMES.PROBE,
    points: world,
    spectralToolVariant: 'probe',
    spectralValues: { peakV_cmS: toCmS(args.ai.value, args.ai.unit) },
    withPolyline: false,
  });
}

/** Velocity within which a vertex counts as part of the envelope's flat edge.
 *  Expressed as a velocity, not a pixel count: physicalDeltaY is cm/s per pixel,
 *  so the same threshold means the same thing whatever the sweep speed. */
const EDGE_TOLERANCE_CM_S = 5;

/** Region calibration for this viewport, or null when none resolves. */
function resolveCalibration(
  viewport: { getCurrentImageId?: () => string | undefined },
  at?: [number, number]
) {
  const imageId = viewport.getCurrentImageId?.() ?? '';
  const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
    | UltrasoundRegionsMeta
    | undefined;
  const { region } = resolveSpectralRegion(regionsMeta?.regions, at);
  return region ? buildCalibrationFromRegion(region) : null;
}

/**
 * Hydrate a doppler VTI envelope as a VTITrace annotation — renders the open
 * polyline contour + VTI value label.
 */
export function hydrateAiDopplerVti(args: HydrateAiDopplerArgs): boolean {
  const ann = args.ai.annotation!;
  // Reopen a closed polygon onto the zero-velocity line before converting to
  // world. referencePixelY0 and the envelope share the DICOM's pixel space, so
  // this has to happen here rather than after the world transform.
  const first = ann.points[0];
  const cal = resolveCalibration(args.viewport, first && [first[0], first[1]]);
  const pixels = cal
    ? openEnvelopeOnBaseline(ann.points, cal.referencePixelY0, {
        edgeTolerancePx: Math.max(
          1,
          EDGE_TOLERANCE_CM_S / Math.abs(cal.physicalDeltaY || 1)
        ),
      })
    : ann.points;
  const world = pixelPointsToWorld(
    args.viewport,
    pixels.map((p) => [...p] as Types.Point3)
  );
  if (!world) return false;
  return addDopplerAnnotation({
    ...args,
    referencedImageId: resolveDopplerImageId(
      args.viewport,
      ann.dicom,
      ann.frameNumber
    ),
    toolName: SPECTRAL_DOPPLER_TOOL_NAMES.VTI,
    points: world,
    spectralToolVariant: 'vti',
    spectralValues: { vti_cm: args.ai.value },
    withPolyline: true,
  });
}
